import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
} from '@amzn/awsui-components-console';
import { SearchState } from '../SearchPage';
import { generateLabel } from '../hooks/useSavedSearches';
import type { SaveResult } from 'src/common/savedSearches';
import styles from './SearchPanelFooter.module.scss';

interface SearchPanelFooterProps {
  readonly currentSearchState: SearchState;
  /** Whether the current state may be submitted — see `SearchPage`. */
  readonly canSearch: boolean;
  /** Whether "Clear all" has anything to reset — see `SearchPage`. */
  readonly canClear: boolean;
  readonly onSearch: () => void;
  readonly onClear: () => void;
  /** Why the search is refused, or `undefined` when it is not. */
  readonly blockedReason?: string;
  /**
   * Id given to the rendered `blockedReason`, so the query input can point its
   * `aria-describedby` at it. Supplied by `SearchPage` because both ends of that
   * association live in different components.
   */
  readonly blockedReasonId: string;
  /**
   * Persists the current search under a name. Supplied by `SearchPage`, which
   * owns the backend-backed saved-searches hook; the footer stays presentational
   * and only drives the modal. Resolves with `success: false` (and a reason) when
   * the save is rejected, so the modal can surface it and stay open.
   */
  readonly onSaveSearch: (
    state: SearchState,
    name: string,
  ) => Promise<SaveResult>;
}

/** Maximum character length for a saved search name */
const MAX_NAME_LENGTH = 50;

/**
 * The search panel's action band: why the search is blocked on the left, and the
 * controls that act on the whole panel on the right — Clear all, Save this search,
 * Search.
 *
 * All three are panel-scoped rather than row-scoped ("Clear all" resets the
 * dropdowns, the query text AND Exact match), which is what earns them a footer of
 * their own instead of a place beside the input.
 *
 * The save-search `Modal` lives here with the button that opens it, so the button
 * and the state it drives stay in one component.
 */
const SearchPanelFooter: React.FC<SearchPanelFooterProps> = ({
  currentSearchState,
  canSearch,
  canClear,
  onSearch,
  onClear,
  blockedReason,
  blockedReasonId,
  onSaveSearch,
}) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const handleSaveSearchClick = useCallback(() => {
    // Pre-populate with auto-generated label as a suggestion
    setSaveSearchName(generateLabel(currentSearchState));
    setShowSaveModal(true);
  }, [currentSearchState]);

  const handleSaveConfirm = useCallback(async () => {
    const trimmedName = saveSearchName.trim();
    if (trimmedName.length === 0) return;
    setIsSaving(true);
    setSaveError('');
    const result = await onSaveSearch(currentSearchState, trimmedName);
    setIsSaving(false);
    if (result.success) {
      setShowSaveModal(false);
      setSaveSearchName('');
      return;
    }
    setSaveError(
      result.duplicateLabel
        ? 'A saved search with this name and filters already exists.'
        : result.loadFailed
          ? "Couldn't load saved searches. Please refresh the page."
          : result.notReady
            ? 'Saved searches are still loading. Please try again in a moment.'
            : "Couldn't save this search. Please try again.",
    );
  }, [saveSearchName, currentSearchState, onSaveSearch]);

  const handleSaveCancel = useCallback(() => {
    setShowSaveModal(false);
    setSaveSearchName('');
    setSaveError('');
  }, []);

  const canSave =
    currentSearchState.queryText.trim().length > 0 ||
    Object.values(currentSearchState.filters).some((v) => v && v.length > 0);

  const isSaveDisabled = saveSearchName.trim().length === 0;

  return (
    <>
      <div className={styles.footer} data-testid="search-panel-footer">
        {/*
          `Box variant="small"` in the inactive text colour — NEUTRAL, not the red
          error treatment the mock shows. An unmet minimum is the pristine state of
          the page on first load, so an error would flag a mistake the user has not
          made yet. It still satisfies the requirement that a blocked search explain
          itself without a hover, and it disappears as soon as a criterion is
          supplied.

          Deliberately NOT Button `disabledReason`. That is a perfectly valid
          Cloudscape pattern — it swaps native `disabled` for `aria-disabled` on
          purpose, so the button stays focusable and its tooltip reachable. It is
          simply the weaker fit here: `disabledReason` describes the BUTTON, whereas
          this message is wired to the query INPUT via `ariaDescribedby` (the id
          below). Since a natively disabled button leaves the tab order, keyboard
          users land on the query input — the field they need to act on — and hear
          the reason there, with no hover or tooltip involved.

          The reason is panel-level rather than field-level: the criterion can be
          satisfied EITHER by the query input OR by any filter dropdown.
        */}
        <div className={styles.message}>
          {blockedReason && (
            /* `Box`'s own `id` prop is deprecated, so the id goes on an inner span. */
            <Box variant="small" color="text-status-inactive">
              <span id={blockedReasonId}>{blockedReason}</span>
            </Box>
          )}
        </div>

        <div className={styles.actions}>
          {/*
            Rendered as a hyperlink via Cloudscape `Button variant="link"` rather
            than an anchor or the `Link` component: it navigates nowhere, it has to
            keep `onClick`/`disabled` semantics, and only a real `button` conveys
            the disabled state to assistive technology.
          */}
          <Button variant="link" onClick={onClear} disabled={!canClear}>
            Clear all
          </Button>
          <Button
            variant="normal"
            onClick={handleSaveSearchClick}
            disabled={!canSave}
            iconName="star"
          >
            Save this search
          </Button>
          <Button variant="primary" onClick={onSearch} disabled={!canSearch}>
            Search
          </Button>
        </div>
      </div>

      <Modal
        visible={showSaveModal}
        onDismiss={handleSaveCancel}
        header="Save search"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleSaveCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveConfirm}
                disabled={isSaveDisabled || isSaving}
                loading={isSaving}
              >
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField
          label="Search name"
          errorText={saveError || undefined}
          constraintText={`${saveSearchName.length}/${MAX_NAME_LENGTH} characters`}
        >
          <Input
            value={saveSearchName}
            onChange={({ detail }) =>
              setSaveSearchName(detail.value.slice(0, MAX_NAME_LENGTH))
            }
            placeholder="Enter a name for this saved search"
            ariaLabel="Saved search name"
          />
        </FormField>
      </Modal>
    </>
  );
};

export default SearchPanelFooter;
